import {
	CloseThin,
	MagnifyingGlassGold,
	SearchHomeUnfilled
} from '@/resources/assets/images';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
	BusinessQuery,
	Search,
	SearchType
} from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';

import { Input } from '@/components/atoms/Input';
import { debounce } from 'lodash';
import { Popover } from '@/components/atoms/Popover';
import Loader from '@/components/Loader/Loader';
import { FilterComponents, useFilters } from '../../molecules/FilterComponents';
import { useMutateCategoriesSuggestions } from './src/hooks/useQueryCategoriesSuggestions';
import type { FilterProps } from '../../../@types';

const splitSearchToIncludedExcluded = (
	searches: NonNullable<BusinessQuery['searches']>
) => {
	return searches.reduce<{
		excludes: string[];
		includes: string[];
	}>(
		(acc, curr) => {
			if (curr.type === SearchType.INCLUDES) {
				acc.includes.push(curr.value);
				return acc;
			}

			acc.excludes.push(curr.value);
			return acc;
		},
		{
			excludes: [],
			includes: []
		}
	);
};

export const FilterSearch = ({
	appliedFilters,
	onFilterChange
}: FilterProps) => {
	const [searchType, setSearchType] = useState<SearchType>(SearchType.INCLUDES);

	const handleClearSearchFilters = () => {
		sendAnalytics({
			type: 'track',
			eventName: 'User cleared all search filters',
			properties: {
				appliedFilters,
				querySearch: appliedFilters?.searches
			}
		});

		onFilterChange({
			...appliedFilters,
			searches: undefined,
			includes: undefined,
			excludes: undefined
		});
	};

	const shouldShowClearFiltersButton = Boolean(
		appliedFilters?.searches && appliedFilters?.searches?.length > 0
	);

	const handleSelectSuggestion = (suggestion: string) => {
		const setQuery = new Set(appliedFilters?.searches);

		setQuery.add({
			index: setQuery.size,
			type: searchType,
			value: suggestion
		});

		const newQuerySearches = [...setQuery] as NonNullable<
			BusinessQuery['searches']
		>;

		const { includes, excludes } =
			splitSearchToIncludedExcluded(newQuerySearches);

		const newQuery = {
			...appliedFilters,
			searches: newQuerySearches,
			includes,
			excludes
		};

		onFilterChange(newQuery);
	};

	const handleToggleSuggestionType = ({
		value,
		type,
		index
	}: NonNullable<BusinessQuery['searches']>[number]) => {
		const searchesArray: NonNullable<BusinessQuery['searches']> =
			appliedFilters?.searches?.map((search, currindex) => {
				if (currindex === index) {
					const toggledType =
						type === SearchType.INCLUDES
							? SearchType.EXCLUDES
							: SearchType.INCLUDES;

					return { ...search, type: toggledType };
				}
				return search;
			}) || [];

		const { includes, excludes } =
			splitSearchToIncludedExcluded(searchesArray);

		const newQuery = {
			...appliedFilters,
			searches: searchesArray,
			includes,
			excludes
		};

		sendAnalytics({
			type: 'track',
			eventName: 'User switched include/exclude',
			properties: {
				appliedFilters: newQuery,
				invertedFilter: value,
				querySearch: searchesArray,
				excludedFromSearch: excludes,
				includedInSearch: includes
			}
		});

		onFilterChange(newQuery);
	};

	const handleRemoveTag = (value: string) => {
		sendAnalytics({
			type: 'track',
			eventName: 'User removed single filter',
			properties: {
				appliedFilters,
				removedFilter: value,
				querySearch: appliedFilters?.searches,
				excludedFromSearch: appliedFilters?.excludes,
				includedInSearch: appliedFilters?.includes
			}
		});

		if (appliedFilters?.searches?.length === 1) {
			onFilterChange({
				...appliedFilters,
				searches: undefined,
				includes: undefined,
				excludes: undefined
			});

			return;
		}

		const searches =
			appliedFilters?.searches
				?.filter(search => search.value !== value)
				.map((search, idx) => ({
					...search,
					index: idx
				})) || [];

		const { includes, excludes } = splitSearchToIncludedExcluded(searches);

		const queryNew = {
			...appliedFilters,
			searches,
			includes,
			excludes
		};

		onFilterChange(queryNew);
	};

	return (
		<FilterComponents.Wrapper moduleName="search">
			<FilterComponents.Header
				showClearFiltersButton={shouldShowClearFiltersButton}
				activeFiltersCount={appliedFilters?.searches?.length || 0}
				icon={SearchHomeUnfilled}
				title="Search"
				onClearFilters={handleClearSearchFilters}
			/>

			<FilterComponents.Content>
				<p>Search for business names or categories</p>

				<section className="flex items-center gap-3">
					<label className="flex cursor-pointer items-center gap-1">
						<Input
							type="radio"
							size="sm"
							className="peer"
							name="search-type"
							value="include"
							checked={searchType === SearchType.INCLUDES}
							onChange={() => setSearchType(SearchType.INCLUDES)}
						/>

						<p>Include</p>
					</label>

					<label className="flex cursor-pointer items-center gap-1">
						<Input
							type="radio"
							size="sm"
							className="peer"
							name="search-type"
							value="exclude"
							checked={searchType === SearchType.EXCLUDES}
							onChange={() => setSearchType(SearchType.EXCLUDES)}
						/>

						<p>Exclude</p>
					</label>
				</section>

				<section className="relative">
					<SearchSuggestions
						searches={appliedFilters?.searches || []}
						onSuggestionSelect={handleSelectSuggestion}
					/>
				</section>

				<section className="flex flex-wrap gap-1">
					{appliedFilters?.searches?.map(search => {
						const { type, value, index } = search;

						return (
							<span
								key={value + type + index}
								className="flex h-7 w-max flex-col items-center rounded-2xl border border-solid border-black bg-dm-black3 px-3 py-1 text-xs text-dark-grey"
							>
								<button
									type="button"
									className="flex gap-1"
									onClick={() =>
										handleToggleSuggestionType(search)
									}
								>
									<span
										data-type={
											type === 0 ? 'include' : 'exclude'
										}
										className="data-[type=exclude]:text-red data-[type=include]:text-green"
									>
										{type === 0 ? 'Includes' : 'Excludes'}
									</span>

									{value}
								</button>

								<CloseThin
									onClick={() => handleRemoveTag(value)}
									className="w-h-3 h-3 cursor-pointer text-dm-pure-white"
								/>
							</span>
						);
					})}
				</section>
			</FilterComponents.Content>
		</FilterComponents.Wrapper>
	);
};

type SearchSuggestionsProps = {
	searches: Search[];
	onSuggestionSelect: (suggestion: string) => void;
};

const SearchSuggestions = ({
	searches,
	onSuggestionSelect
}: SearchSuggestionsProps) => {
	const { isOpen } = useFilters();

	const [searchBarValue, setSearchBarValue] = useState('');
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);

	const { mutate, data, isLoading } = useMutateCategoriesSuggestions();

	const filteredSuggestions = useMemo(() => {
		const categorySuggestions = data?.data.data;

		return (
			categorySuggestions?.filter(
				name =>
					!searches?.some(
						filter =>
							filter.value.toLowerCase() === name.toLowerCase()
					)
			) || []
		);
	}, [searches, data]);

	const debouncedFetchSuggestions = useMemo(
		() => debounce((prefix: string) => mutate(prefix), 300),
		[mutate]
	);

	const handleUpdateUserInput = (e: ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;

		setSearchBarValue(value);
		debouncedFetchSuggestions(value);

		if (value.length >= 2) {
			setIsPopoverOpen(true);
		}
	};

	const handleSetInitialState = () => {
		setSearchBarValue('');
		setIsPopoverOpen(false);
	};

	const handleSelectSuggestion = (suggestion: string) => {
		onSuggestionSelect(suggestion);
		handleSetInitialState();
	};

	useEffect(() => {
		return () => {
			debouncedFetchSuggestions.cancel();
		};
	}, [debouncedFetchSuggestions]);

	useEffect(() => {
		if (!isOpen) {
			handleSetInitialState();
		}
	}, [isOpen]);

	return (
		<Popover.Root open={isOpen && isPopoverOpen}>
			<Popover.Anchor>
				<MagnifyingGlassGold className="absolute right-4 top-1/2 z-20 -translate-y-1/2" />

				<Input
					type="text"
					size="sm"
					placeholder="Search"
					value={searchBarValue}
					onChange={handleUpdateUserInput}
				/>
			</Popover.Anchor>

			<Popover.Content
				onOpenAutoFocus={e => e.preventDefault()}
				className="max-h-40 max-w-56 overflow-hidden overflow-y-auto border-dm-gray bg-dm-black3 p-0"
			>
				<ul className="flex h-full w-full flex-col">
					<li className="flex w-full items-center px-2 py-1.5">
						<button
							type="button"
							className="w-full text-start"
							onClick={() =>
								handleSelectSuggestion(searchBarValue)
							}
						>
							{searchBarValue}
						</button>
					</li>

					{isLoading ? (
						<li className="flex w-full items-center justify-center px-2 py-1.5">
							<Loader />
						</li>
					) : (
						filteredSuggestions.map((suggestion: string) => (
							<li
								key={suggestion}
								className="flex w-full items-center border-t border-dm-gray px-2 py-1.5"
							>
								<button
									type="button"
									className="w-full text-start"
									onClick={() =>
										handleSelectSuggestion(suggestion)
									}
								>
									{suggestion}
								</button>
							</li>
						))
					)}
				</ul>
			</Popover.Content>
		</Popover.Root>
	);
};
